// src/components/Navbar.js

import React from 'react';
import { Layout, Menu, Button, Dropdown } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HomeOutlined,
  BuildOutlined,
  CalculatorOutlined,
  DollarOutlined,
  TruckOutlined,
  ContainerOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  ToolOutlined,
  CompassOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const { Header } = Layout;

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userRole, signOut, canAccessModule, ROLES } = useAuth();

  // Navbar styling (Unchanged)
  const navbarStyle = {
    background: '#394A3F',
    borderBottom: '1px solid #8A9B8F',
    padding: '0 24px',
    boxShadow: 'none',
    height: 64,
    display: 'flex',
    justifyContent: 'space-between'
  };
  const menuStyle = {
    backgroundColor: 'transparent',
    border: 'none',
    display: 'flex',
    justifyContent: 'center',
    minWidth: '800px'
  };
  const logoStyle = {
    color: '#FFFFFF',
    fontWeight: 300,
    fontSize: '18px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    marginRight: '40px',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    height: '64px'
  };
  const userDropdownButton = {
    background: 'transparent',
    color: 'white',
    border: 'none',
    fontWeight: 300,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    height: '64px',
    paddingRight: '20px'
  };
  // (End of styling)

  // Filter menu items based on user role and access permissions
  const getFilteredMenuItems = () => {
    const baseMenuItems = [
      {
        key: '/',
        icon: <HomeOutlined />,
        label: 'Home'
      }
    ];
    
    // Company menu
    if (canAccessModule('company-setup') || canAccessModule('manage-company-setup')) {
      const companyChildren = [];
      if (canAccessModule('company-setup')) {
        companyChildren.push({ key: "/company-setup", label: "Company Setup" });
      }
      if (canAccessModule('manage-company-setup')) {
        companyChildren.push({ key: "/manage-company-setup", label: "Manage Company" });
      }
      if (companyChildren.length > 0) {
        baseMenuItems.push({
          key: 'company',
          icon: <BuildOutlined />,
          label: 'Company',
          children: companyChildren
        });
      }
    }
    
    // Salary Management menu
    const truckSalaryChildren = [];
    const leadSalaryChildren = [];
    const recorderSalaryChildren = [];
    const excavatorSalaryChildren = [];
    
    if (canAccessModule('salary-calculator')) {
      truckSalaryChildren.push({ key: '/salary-calculator', label: 'Calculate' });
    }
    if (canAccessModule('manage-monthly-calculations')) {
      truckSalaryChildren.push({ key: '/manage-monthly-calculations', label: 'Manage' });
    }
    if (canAccessModule('lead-info-form')) {
      leadSalaryChildren.push({ key: '/lead-info-form', label: 'Calculate' });
    }
    if (canAccessModule('manage-lead-salary')) {
      leadSalaryChildren.push({ key: '/manage-lead-salary', label: 'Manage' });
    }
    if (canAccessModule('recorder-info-form')) {
      recorderSalaryChildren.push({ key: '/recorder-info-form', label: 'Calculate' });
    }
    if (canAccessModule('manage-recorder-salary')) {
      recorderSalaryChildren.push({ key: '/manage-recorder-salary', label: 'Manage' });
    }
    if (canAccessModule('excavator-info-form')) {
      excavatorSalaryChildren.push({ key: '/excavator-info-form', label: 'Calculate' });
    }
    if (canAccessModule('manage-excavator-salary')) {
      excavatorSalaryChildren.push({ key: '/manage-excavator-salary', label: 'Manage' });
    }
    
    const salaryChildren = [];
    if (truckSalaryChildren.length > 0) {
      salaryChildren.push({ key: 'truck-salary', label: 'Truck', children: truckSalaryChildren });
    }
    if (leadSalaryChildren.length > 0) {
      salaryChildren.push({ key: 'lead-salary', label: 'Lead', children: leadSalaryChildren });
    }
    if (recorderSalaryChildren.length > 0) {
      salaryChildren.push({ key: 'recorder-salary', label: 'Recorder', children: recorderSalaryChildren });
    }
    if (excavatorSalaryChildren.length > 0) {
      salaryChildren.push({ key: 'excavator-salary', label: 'Excavator', children: excavatorSalaryChildren });
    }
    
    if (salaryChildren.length > 0) {
      baseMenuItems.push({
        key: 'salary',
        icon: <DollarOutlined />,
        label: 'Salary Management',
        children: salaryChildren
      });
    }
    
    // Operations menu
    const operationsChildren = [];
    const truckOpsChildren = [];
    
    if (canAccessModule('diesel-monthly')) {
      operationsChildren.push({ key: '/diesel-monthly', icon: <ContainerOutlined />, label: 'Monthly Diesel' });
    }
    if (canAccessModule('truck-operations')) {
       truckOpsChildren.push({ key: '/fleet-live', icon: <CompassOutlined />, label: 'Live GPS Map' });
    }
    if (canAccessModule('truck-operations')) {
      truckOpsChildren.push({ key: '/truck-operations', label: 'Daily Operations' });
    }
    if (canAccessModule('truck-operations/records')) {
      truckOpsChildren.push({ key: '/truck-operations/records', label: 'Manage Records' });
    }
    if (canAccessModule('truck-monthly')) {
      truckOpsChildren.push({ key: '/truck-monthly', label: 'Monthly Overview' });
    }
    if (truckOpsChildren.length > 0) {
      operationsChildren.push({ key: 'daily-ops', icon: <TruckOutlined />, label: 'Truck Operations', children: truckOpsChildren });
    }
    if (operationsChildren.length > 0) {
      baseMenuItems.push({
        key: 'operations',
        icon: <CalculatorOutlined />,
        label: 'Operations',
        children: operationsChildren
      });
    }

    // --- (UPDATED MAINTENANCE MENU) ---
    if (userRole === ROLES.ADMIN) {
      baseMenuItems.push({
        key: 'maintenance',
        icon: <ToolOutlined />,
        label: 'Maintenance',
        children: [
          {
            key: '/maintenance-dashboard',
            label: 'Analytics Dashboard'
          },
          {
            key: '/maintenance-log',
            label: 'Breakdown Log' // <-- Renamed for clarity
          },
          {
            key: '/service-log', // <-- NEW
            label: 'Service Log'
          },
          {
            key: '/inspection-log', // <-- NEW
            label: 'Inspection Log'
          }
        ]
      });
    }

    // --- (UPDATED USER MANAGEMENT MENU) ---
    if (canAccessModule('user-management')) {
      baseMenuItems.push({
        key: 'user-management-group',
        icon: <UserOutlined />,
        label: 'User Management',
        children: [
          {
            key: '/user-management',
            label: 'Manage Admin Users'
          },
          {
            key: '/mechanic-setup',
            label: 'Create Mechanic'
          },
          {
            key: '/mechanic-management',
            label: 'Manage Mechanics'
          }
        ]
      });
    }
    
    return baseMenuItems;
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error("Failed to log out:", error);
    }
  };

  const userDropdownItems = [
    {
      key: 'role',
      label: `Role: ${userRole?.toUpperCase() || 'Not Assigned'}`,
      disabled: true,
    },
    {
      key: 'profile',
      label: 'Profile Settings',
      icon: <SettingOutlined />,
      onClick: () => navigate('/profile'),
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    }
  ];

  return (
    <Header style={navbarStyle}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={logoStyle}>
          ADMINISTRATION
        </div>
        {currentUser && (
          <Menu
            theme="dark"
            mode="horizontal"
            selectedKeys={[location.pathname]}
            onClick={({ key }) => navigate(key)}
            items={getFilteredMenuItems()}
            style={menuStyle}
            className="custom-navbar"
          />
        )}
      </div>
      
      {currentUser && (
        <Dropdown menu={{ items: userDropdownItems }} placement="bottomRight">
          <Button style={userDropdownButton} icon={<UserOutlined />}>
            {currentUser.email}
          </Button>
        </Dropdown>
      )}
      
      {/* CSS (Unchanged) */}
      <style jsx="true">{`
        .custom-navbar.ant-menu {
          background-color: transparent;
        }
        
        .custom-navbar.ant-menu .ant-menu-item,
        .custom-navbar.ant-menu .ant-menu-submenu {
          color: #F0F0F0;
          font-weight: 300;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin: 0 4px;
        }
        
        /* Override hover color */
        .custom-navbar.ant-menu-dark .ant-menu-item:hover,
        .custom-navbar.ant-menu-dark .ant-menu-submenu:hover,
        .custom-navbar.ant-menu-dark .ant-menu-item-active,
        .custom-navbar.ant-menu-dark .ant-menu-submenu-active,
        .custom-navbar.ant-menu-dark .ant-menu-item-open,
        .custom-navbar.ant-menu-dark .ant-menu-submenu-open,
        .custom-navbar.ant-menu-dark .ant-menu-item-selected,
        .custom-navbar.ant-menu-dark .ant-menu-submenu-selected {
          background-color: rgba(163, 172, 165, 0.2) !important; /* Light sage with opacity */
          color: white;
          border-radius: 10px
        }
        
        /* Override submenu popup background */
        .ant-menu-dark.ant-menu-submenu-popup {
          background-color: #394A3F !important;
        }
        
        .custom-navbar.ant-menu-dark .ant-menu-item-selected {
          background-color: rgba(163, 172, 165, 0.3) !important;
          border-bottom: 2px solid #A3ACA5;
        }
        
        /* Submenu popup styling */
        .ant-menu-dark .ant-menu-submenu-popup {
          background: #394A3F !important;
          border: 1px solid #8A9B8F;
          border-radius: 0;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }
        
        /* Override submenu popup items */
        .ant-menu-dark .ant-menu-submenu-popup .ant-menu-item {
          text-transform: uppercase;
          letter-spacing: 1px;
          font-weight: 300;
        }
        
        .ant-menu-dark .ant-menu-submenu-popup .ant-menu-item:hover {
          background-color: rgba(163, 172, 165, 0.2) !important;
        }
        
        /* Override submenu arrow color */
        .ant-menu-dark .ant-menu-submenu-arrow::before,
        .ant-menu-dark .ant-menu-submenu-arrow::after {
          background-color: #F0F0F0 !important;
        }
        
        .ant-menu-dark .ant-menu-submenu:hover > .ant-menu-submenu-title > .ant-menu-submenu-arrow::before,
        .ant-menu-dark .ant-menu-submenu:hover > .ant-menu-submenu-title > .ant-menu-submenu-arrow::after {
          background-color: white !important;
        }
        
        /* Fix the top-level menu horizontal line */
        .ant-menu-horizontal::after {
          display: none !important;
        }
      `}</style>
    </Header>
  );
};

export default Navbar;